import { stumbling } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type TableFilter,
  filterOperators,
  isArrayContainsOperator,
  isLabelsArrayOperator,
  isNameContainsAnyOperator,
  isNameNotContainsAnyOperator,
  isRoutingIpv6TableFilter,
  isRoutingTableFilter,
  parseArrayContainsFilterValue,
  parseDisplayTokensToLabelFilters,
  parseNameOnlyLabelFilters,
} from '@ipfabric/utils';
import { getDateForFilter } from 'components/ApiTable/helpers/getDateForFilter';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { measureUnitValueConvertor } from 'components/Table/components/SimpleFilter/helpers/filterValueConvertors/measureUnitValueConvertor';
import { uptimeToNumber } from 'components/Table/helpers/uptimeToNumber';
import {
  isValidCidr,
  isValidCidrIpv6,
  isValidIp,
  isValidIpv6,
} from 'utils/formValidation/validations';
import {
  type GroupFilter,
  type RuleFilter,
  isGroupFilter,
} from './useAdvancedFilter';

const generic = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filterOperators.empty.some((o) => o === filter.operator)) {
    return undefined;
  }
  if (
    isArrayContainsOperator(filter.operator) ||
    isLabelsArrayOperator(filter.operator)
  ) {
    return parseArrayContainsFilterValue(filter.value).length === 0
      ? 'Required'
      : undefined;
  }
  return filter.value === '' ? 'Required' : undefined;
};

const labels = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filterOperators.empty.some((o) => o === filter.operator)) {
    return undefined;
  }

  if (isNameContainsAnyOperator(filter.operator)) {
    return parseNameOnlyLabelFilters(filter.value).length === 0
      ? 'Required'
      : undefined;
  }

  if (isNameNotContainsAnyOperator(filter.operator)) {
    return parseNameOnlyLabelFilters(filter.value).length === 0
      ? 'Required'
      : undefined;
  }

  if (
    filter.operator === 'containsAny' ||
    filter.operator === 'notContainsAny'
  ) {
    return parseDisplayTokensToLabelFilters(filter.value).length === 0
      ? 'Required'
      : undefined;
  }

  return generic(filter);
};

const ip = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filter.operator === 'eq' || filter.operator === 'neq') {
    return !isValidIp(filter.value) ? 'Invalid IP' : undefined;
  }
  if (filter.operator === 'cidr') {
    return !isValidCidr(filter.value) ? 'Invalid CIDR' : undefined;
  }
  return generic(filter);
};

const ipv6 = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filter.operator === 'eq' || filter.operator === 'neq') {
    return !isValidIpv6(filter.value) ? 'Invalid IPv6' : undefined;
  }
  if (filter.operator === 'cidr') {
    return !isValidCidrIpv6(filter.value) ? 'Invalid IPv6 CIDR' : undefined;
  }
  return generic(filter);
};

const dateTime = (
  filter: RuleFilter,
  ruleContext: RuleContext,
): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }
  if (filterOperators.dateTime.some((o) => o === filter.operator)) {
    if (filter.operator === 'nowGt' || filter.operator === 'nowLt') {
      const parsedValue = Number.parseInt(filter.value, 10);
      return Number.isNaN(parsedValue) || parsedValue < 0
        ? 'Invalid Number'
        : undefined;
    }
    const parsedDate = getDateForFilter(filter.value, ruleContext);
    return parsedDate === null ? 'Invalid Date' : undefined;
  }
  return generic(filter);
};

const uptime = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filterOperators.uptime.some((o) => o === filter.operator)) {
    return uptimeToNumber(filter.value) === null ? 'Invalid Uptime' : undefined;
  }
  return generic(filter);
};

const number = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filterOperators.number.some((o) => o === filter.operator)) {
    return Number.isNaN(measureUnitValueConvertor.toNumber(filter.value))
      ? 'Invalid value'
      : undefined;
  }
  return generic(filter);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: complex decision
const routing = (filter: RuleFilter): string | undefined => {
  if (!filter.operator) {
    return undefined;
  }

  if (filter.operator === 'ip') {
    if (isRoutingTableFilter(filter.column?.filter)) {
      return !isValidIp(filter.value) ? 'Invalid IP' : undefined;
    }
    if (isRoutingIpv6TableFilter(filter.column?.filter)) {
      return !isValidIpv6(filter.value) ? 'Invalid IPv6' : undefined;
    }
    return stumbling('Not supported version of IP')(undefined);
  }
  if (
    filter.operator === 'nsect' ||
    filter.operator === 'sect' ||
    filter.operator === 'gte' ||
    filter.operator === 'gt' ||
    filter.operator === 'lte' ||
    filter.operator === 'lt'
  ) {
    if (isRoutingTableFilter(filter.column?.filter)) {
      return !isValidCidr(filter.value) ? 'Invalid CIDR' : undefined;
    }
    return stumbling('Not supported version of CIDR')(undefined);
  }
  return generic(filter);
};

export const isFilterInvalid = (filter: GroupFilter | RuleFilter): boolean => {
  if (isGroupFilter(filter)) {
    return filter.filters.some(isFilterInvalid);
  }
  if (filter.column?.key === 'device.attributes') {
    // keep hardcoded until there is only one kind of such key
    if (filter.dynamicFieldError) {
      return true;
    }
  }
  return !!filter.error || !!filter.unsupportedOperatorError;
};

export const filterHasEmptyOperator = (
  filter: GroupFilter | RuleFilter,
): boolean => {
  if (isGroupFilter(filter)) {
    return filter.filters.some(filterHasEmptyOperator);
  }
  return !filter.operator || !!filter.unsupportedOperatorError;
};

export const validateRule: {
  [Type in TableFilter['type']]: (
    filter: RuleFilter,
    ruleContext: RuleContext,
  ) => string | undefined;
} = {
  text: generic,
  number,
  select: generic,
  ip,
  uptime,
  asn: generic,
  boolean: generic,
  routing,
  routingIpv6: routing,
  ipv6,
  object: generic,
  dateTime,
  labels,
};
